import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import 'react-tooltip/dist/react-tooltip.css';
import { isEmpty } from 'lodash';
import { Navigate, useNavigate } from 'react-router-dom';

import { API_CONFIG } from 'shared/constants/constants';
import { useWhitelabelConfigStatus } from 'shared/hooks/useWhitelabelConfigStatus';
import httpService from 'shared/services/http.service';
import localizationConstants from 'shared/util/translation/constants';
import { Translate } from 'shared/util/translation/translate';
import { checkPermission } from 'shared/hoc/permission/permissionManager';
import { adminPermissionMapping } from 'shared/constants/permissionConstant';
import { INavArray, ISubMenuType, State } from 'shared/interface';
import { createAction, debounce, getAdjustedDimensions } from 'shared/util/utility';
import { getDefaultLogoUrl, resolveLogoUrl } from 'shared/util/logoUrl';
import * as actionType from 'store/action-types';
import * as CountAction from 'store/alertCount.action';

import NavItem from './navItem';
import { DictionaryIcon, FaqIcon, InfoIcon, MatflixLogo, MinimizeSideMenuIcon, ShortMatflixLogo, VideoIcon } from 'shared/icons/icon';
import PartnerLinkSlider from './partnerLinkSlider';
import SupportFormModal from './supportFormModal';

interface IProps {
	className: string;
}

const imageWidth = 230;
const imageHeight = 50;
const small_Image_Height = 25;
const small_Image_width = 26;

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url);
const normalizeExternalNavHref = (url: string) => {
	const trimmedUrl = String(url || '').trim();
	if (!trimmedUrl) {
		return '#';
	}
	if (isExternalUrl(trimmedUrl) || trimmedUrl.startsWith('/')) {
		return trimmedUrl;
	}
	return trimmedUrl.includes('.') ? `https://${trimmedUrl}` : `/${trimmedUrl}`;
};

const SideNav: React.FC<IProps> = (props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const authData = useSelector((state: State) => state.auth.authData);
	const userData = useSelector((state: State) => state.auth.userData, shallowEqual);
	const safeUserData = userData || ({} as typeof userData);
	const safeAdmin = Array.isArray(safeUserData?.admin) ? safeUserData.admin : [];
	const show_partner_section = useSelector((state: State) => state.sideNavConfig.show_partner_section);
	const { material_status, news_status, whitelabelId, forecast_status, pdfModule_status } = useWhitelabelConfigStatus();
	const [openStatus, setOpenStatus] = useState<Record<string, boolean>>({});
	const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
	const [isOpenSidebar, setIsOpenSidebar] = useState(true);
	const [fullSize, setFullSize] = useState({ width: imageWidth, height: imageHeight });
	const [smallSize, setSmallSize] = useState({ width: small_Image_width, height: small_Image_Height });
	const [fullLogoFailed, setFullLogoFailed] = useState(false);
	const [smallLogoFailed, setSmallLogoFailed] = useState(false);
	const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

	const fullLogoSrc = useMemo(() => {
		if (fullLogoFailed) {
			return getDefaultLogoUrl('full');
		}
		return resolveLogoUrl(safeUserData?.whitelable_config?.full_logo, 'full');
	}, [fullLogoFailed, safeUserData?.whitelable_config?.full_logo]);

	const smallLogoSrc = useMemo(() => {
		if (smallLogoFailed) {
			return getDefaultLogoUrl('small');
		}
		return resolveLogoUrl(safeUserData?.whitelable_config?.small_logo, 'small');
	}, [smallLogoFailed, safeUserData?.whitelable_config?.small_logo]);

	const whitelabelNavItems = useMemo(
		() =>
			[
				{
					icon: safeUserData?.whitelable_config?.nav_icon_1,
					name: safeUserData?.whitelable_config?.nav_name_1,
					link: safeUserData?.whitelable_config?.nav_link_1
				},
				{
					icon: safeUserData?.whitelable_config?.nav_icon_2,
					name: safeUserData?.whitelable_config?.nav_name_2,
					link: safeUserData?.whitelable_config?.nav_link_2
				}
			].filter((item) => String(item.name || '').trim() && String(item.link || '').trim()),
		[
			safeUserData?.whitelable_config?.nav_icon_1,
			safeUserData?.whitelable_config?.nav_icon_2,
			safeUserData?.whitelable_config?.nav_link_1,
			safeUserData?.whitelable_config?.nav_link_2,
			safeUserData?.whitelable_config?.nav_name_1,
			safeUserData?.whitelable_config?.nav_name_2
		]
	);
	const whitelabelNavText = String(safeUserData?.whitelable_config?.nav_text || '').trim();
	const hasWhitelabelNavContent = !isEmpty(whitelabelNavItems) || Boolean(whitelabelNavText);

	useEffect(() => {
		const { full_width, full_height, small_width, small_height } = safeUserData?.whitelable_config || {};

		// Full size adjustment
		const adjustedFull = getAdjustedDimensions(full_width || imageWidth, full_height || imageHeight, imageWidth, imageHeight);
		setFullSize(adjustedFull);

		// Small size adjustment
		const adjustedSmall = getAdjustedDimensions(
			small_width || small_Image_width,
			small_height || small_Image_Height,
			small_Image_width,
			small_Image_Height
		);
		setSmallSize(adjustedSmall);
	}, [safeUserData?.whitelable_config]);

	useEffect(() => {
		setFullLogoFailed(false);
		setSmallLogoFailed(false);
	}, [safeUserData?.whitelable_config?.full_logo, safeUserData?.whitelable_config?.small_logo]);

	const alertCount = useSelector((state: State) => state.alertCount);

	const getShowPartnerLink = useCallback(() => {
		httpService
			.get(API_CONFIG.path.getShowPartnerLink)
			.then((response) => {
				dispatch(createAction(actionType.UPDATE_SHOW_PARTNER_SECTION, response));
			})
			.catch((error) => {
				console.error(error);
			});
	}, [dispatch]);

	useEffect(() => {
		getShowPartnerLink();
	}, [getShowPartnerLink]);

	useEffect(() => {
		CountAction.fetchBaseCount(dispatch);
		const interval = setInterval(() => {
			CountAction.fetchBaseCount(dispatch);
		}, 10000);

		return () => clearInterval(interval);
	}, [dispatch]);

	const onresize = useCallback(() => {
		if (window.innerWidth <= 992) {
			document.body.classList.add('mini-navbar');
			setIsOpenSidebar(false);
		} else {
			document.body.classList.remove('mini-navbar');
			setIsOpenSidebar(true);
		}
	}, []);

	const throttleResize = useCallback(debounce(onresize, 300), []);

	useEffect(() => {
		throttleResize();
		window.addEventListener('resize', throttleResize);
		return () => {
			window.removeEventListener('resize', throttleResize);
		};
	}, [throttleResize]);

	const navArray = useMemo(() => {
		const copyOfNavarray: INavArray[] = JSON.parse(JSON.stringify(NAVARRAY));
		const conditions = [
			{ id: 'admin', condition: !checkPermission({ permission: adminPermissionMapping.superDataUserAdmin }) },
			{ id: 'tenders', condition: !safeUserData?.isTenderRights },
			{ id: 'costModel', condition: !safeUserData?.isCostModelRights },
			{ id: 'materials', condition: material_status === 'hideall' },
			{ id: 'news', condition: news_status === 'hideall' || news_status === 'active' || news_status === 'active+original' },
			{ id: 'forecasts', condition: forecast_status === 'hideall' },
			{ id: 'pdfModule', condition: !safeUserData?.isPdfModuleRights || pdfModule_status === 'hideall' }
		];

		conditions.forEach(({ id, condition }) => {
			const index = copyOfNavarray.findIndex((item) => item.id === id);
			if (condition) {
				if (index !== -1) {
					if (id === 'news' && (news_status === 'active' || news_status === 'active+original')) {
						copyOfNavarray[index].route = `/news/articleList/0?whitelabelId=${whitelabelId}`;
						(copyOfNavarray[index].subMenu as ISubMenuType[])[0].route = `/news/saveNews?whitelabelId=${whitelabelId}`;
					} else {
						copyOfNavarray.splice(index, 1);
					}
				}
			} else {
				if (id === 'admin' && safeAdmin.length === 1 && safeAdmin.includes('dataAdmin')) {
					copyOfNavarray[index].route = '/admin?tab=data';
				}
			}
		});
		return copyOfNavarray;
	}, [
		safeUserData?.isTenderRights,
		safeAdmin,
		material_status,
		news_status,
		forecast_status,
		whitelabelId,
		safeUserData?.isPdfModuleRights,
		safeUserData?.isCostModelRights,
		pdfModule_status
	]);

	// const handleClickOutside = () => {
	// 	setIsAlertOpen(false);
	// };

	const onClickSideNav = () => {
		setIsAlertOpen(!isAlertOpen);
	};

	const toggleSidebar = useCallback(() => {
		setIsOpenSidebar((isOpen) => {
			const newState = !isOpen;
			if (newState) {
				document.body.classList.remove('mini-navbar');
			} else {
				document.body.classList.add('mini-navbar');
			}
			return newState;
		});
	}, []);

	const toggleIsOpen = (name: string) => {
		setOpenStatus((prevState) => ({
			[name]: !prevState[name]
		}));
	};

	if (!authData?.auth) {
		return <Navigate replace to='/login' />;
	}

	return (
		<nav
			className={`navbar-default navbar-static-side ${props.className || ''} d-flex align-items-center flex-column bg-color--primary`}
			role='navigation'
		>
			<ul className='nav full-width' id='side-menu'>
				<li className='nav-header cursor-pointer d-flex align-items-center justify-content-space-between'>
					<div
						className='profile-element '
						onClick={() => navigate('/dashboard')}
						style={{ width: fullSize.width, height: fullSize.height }}
					>
						{!isEmpty(safeUserData?.whitelable_config) ? (
							<img
								src={fullLogoSrc}
								alt='whitelabel full logo'
								crossOrigin='anonymous'
								onError={() => {
									if (!fullLogoFailed) {
										setFullLogoFailed(true);
									}
								}}
								style={{ objectFit: 'contain' }}
								className='full-width full-height'
							/>
						) : (
							<MatflixLogo className='matflixx-icon' />
						)}
					</div>
					<div className='logo-element' onClick={() => navigate('/dashboard')} style={{ width: smallSize.width, height: smallSize.height }}>
						{!isEmpty(safeUserData?.whitelable_config) ? (
							<img
								src={smallLogoSrc}
								alt='whitelabel small logo'
								crossOrigin='anonymous'
								onError={() => {
									if (!smallLogoFailed) {
										setSmallLogoFailed(true);
									}
								}}
								className='full-width full-height'
							/>
						) : (
							<ShortMatflixLogo />
						)}
					</div>
				</li>
				{!isEmpty(navArray) && !isEmpty(alertCount) && (
					<>
						{navArray.map((nav, index) => (
							<NavItem
								id={nav.id}
								isOpen={openStatus[nav.id]}
								toggleIsOpen={toggleIsOpen}
								key={index}
								menu={nav}
								alertCount={alertCount}
								onClickSideNav={onClickSideNav}
							/>
						))}
						{/* <Tooltip
							anchorId='alert'
							data-tooltip-place='right'
							classNameArrow='display--none'
							className='alert-popup no-padding'
							clickable
						>
							<AlertPopup alertBaseCount={alertBaseCount} fetchAlertBaseCount={fetchAlertBaseCount} onClose={handleClickOutside} />
						</Tooltip> */}
					</>
				)}
			</ul>
			<button
				className='navbar-minimalize top-50 btn navbar-back d-flex justify-content-center align-items-center position--absolute right-0'
				onClick={toggleSidebar}
			>
				<MinimizeSideMenuIcon />
			</button>
			<div
				className={`sidenav-bottom__wrapper full-width d-flex flex-column align-items-start justify-content-end ${!isOpenSidebar && 'mt-70'}`}
			>
				{hasWhitelabelNavContent && (
					<div
						className={`whitelabel-nav-content full-width ${
							show_partner_section && isOpenSidebar ? 'whitelabel-nav-content--with-partner' : 'whitelabel-nav-content--without-partner'
						}`}
					>
						{!isEmpty(whitelabelNavItems) && (
							<ul className='whitelabel-nav-content__list nav full-width'>
								{whitelabelNavItems.map((item, index) => {
									const link = String(item.link || '').trim();
									const href = normalizeExternalNavHref(link);
									return (
										<li className='side-menu-shirnked whitelabel-nav-content__tab' key={`${item.name}-${index}`}>
											<a
												href={href || '#'}
												target='_blank'
												rel='noreferrer'
												className='d-flex align-items-center position--relative'
											>
												{item.icon ? (
													<img
														src={resolveLogoUrl(item.icon, 'small')}
														alt=''
														crossOrigin='anonymous'
														className='whitelabel-nav-content__tab-icon'
													/>
												) : (
													<InfoIcon />
												)}
												<span className='nav-label font--16px ml-10 font-weight--500 line-height--22px'>{item.name}</span>
											</a>
										</li>
									);
								})}
							</ul>
						)}
						{whitelabelNavText && isOpenSidebar && <div className='whitelabel-nav-content__text'>{whitelabelNavText}</div>}
					</div>
				)}
				{show_partner_section && isOpenSidebar && <PartnerLinkSlider />}
				<ul className='bottom-nav nav full-width' id='side-menu'>
					<li className=' side-menu-shirnked'>
						<a
							href='https://www.matflixx.de/glossary'
							target='_blank'
							className='d-flex align-items-center position--relative'
							rel='noreferrer'
						>
							<DictionaryIcon />
							<span className='nav-label font--16px ml-10 font-weight--500 line-height--22px'>
								<Translate text={localizationConstants.glossary} />
							</span>
						</a>
					</li>
					<li className=' side-menu-shirnked'>
						<a
							href='https://youtube.com/playlist?list=PL-OYyyZ8MNYr0PPAL4HNfB9qtmwixLVOz&si=S63Lq8v-C3Nh8JUE'
							target='_blank'
							className='d-flex align-items-center position--relative'
							rel='noreferrer'
						>
							<VideoIcon />
							<span className='nav-label font--16px ml-10 font-weight--500 line-height--22px'>
								<Translate text={localizationConstants.explainerVideos} />
							</span>
						</a>
					</li>
					<li className=' side-menu-shirnked'>
						<a
							href='#support'
							className='d-flex align-items-center position--relative'
							onClick={(event) => {
								event.preventDefault();
								setIsSupportModalOpen(true);
							}}
						>
							<FaqIcon />
							<span className='nav-label font--16px ml-10 font-weight--500 line-height--22px'>
								<Translate text={localizationConstants.support} />
							</span>
						</a>
					</li>
					<li className=' side-menu-shirnked'>
						<a
							href='https://www.matflixx.de/faq'
							target='_blank'
							className='d-flex align-items-center position--relative'
							rel='noreferrer'
						>
							<FaqIcon />
							<span className='nav-label font--16px ml-10 font-weight--500 line-height--22px'>FAQ</span>
						</a>
					</li>
					<li className=' side-menu-shirnked'>
						<a
							href='https://www.matflixx.de/impressum'
							target='_blank'
							className='d-flex align-items-center position--relative'
							rel='noreferrer'
						>
							<InfoIcon />
							<span className='nav-label font--16px ml-10 font-weight--500 line-height--22px'>
								<Translate text={localizationConstants.imprint} />
							</span>
						</a>
					</li>
				</ul>
			</div>
			<SupportFormModal show={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} />
		</nav>
	);
};

export default SideNav;

const NAVARRAY: INavArray[] = [
	{
		id: 'dashboard',
		name: 'dashboard',
		route: '/dashboard'
	},
	{
		id: 'materials',
		name: `${localizationConstants.materials}`,
		route: '/materials/allMaterials',
		subMenu: [
			{
				name: `${localizationConstants.allMaterials}`,
				route: '/materials/allMaterials'
			},
			{
				name: `${localizationConstants.myMaterials}`,
				route: '/materials/myMaterial'
			},
			{
				name: `${localizationConstants.search}`,
				route: '/materials/search'
			}
		]
	},
	{
		id: 'news',
		name: `${localizationConstants.news}`,
		route: `/news/articleList/0`,
		subMenu: [
			{
				name: `${localizationConstants.saveMessage}`,
				route: '/news/saveNews'
			}
		]
	},
	{
		id: 'tenders',
		name: `Tenders & more`,
		route: `/tenders/allTenders`,
		subMenu: [
			{
				name: `${localizationConstants.saveTenders}`,
				route: '/tenders/saveTenders/projects'
			}
		]
	},
	{
		id: 'costModel',
		name: `${localizationConstants.costModel}`,
		route: `/costModel`
	},
	{
		id: 'forecasts',
		name: `${localizationConstants.forecasts}`,
		route: '/forecasts'
	},
	{
		id: 'pdfModule',
		name: `${localizationConstants.pdfModule}`,
		route: '/pdfModule'
	},
	{
		id: 'alert',
		name: `${localizationConstants.alerts}`,
		route: '/alert/allAlerts?tab=material'
	},
	{
		id: 'admin',
		name: `${localizationConstants.admin}`,
		route: '/admin?tab=users'
	}
];
